import Card from "assets/Card.svg";
import useValidationSchema from "hooks/useValidationSchema";
import React, { useCallback, useMemo } from "react";
import { Field, Form } from "react-final-form";
import { DniFinishFormValuesType, DniFinishPropsType } from "./types";
import "./DniFinish.scss";
import { useTranslation } from "react-i18next";
import "../i18n";
import {
  validationSchema,
} from "./schemas";
import { BackButton } from "../backButton/BackButton";
import {
  Button,
  FileInput,
  MultiSelectInput,
  SelectInput,
  TextInput,
} from "@pickit/pickit-components";
import { MAX_FILE_SIZE } from "utils/constants";

const FILE_TYPES_ACCEPTED: (".png" | ".jpg" | ".jpeg")[] = [".png", ".jpg", ".jpeg"];

function validateFile(file: File | null): boolean {
  if (!file) return false;
  const extension = `.${file.type.split("/").pop()}`;
  return file.size <= MAX_FILE_SIZE && (FILE_TYPES_ACCEPTED as readonly string[]).includes(extension);
}

function base64OrBufferToFile(
  name: string,
  data: string | ArrayBuffer
): File {
  let blob: Blob;
  if (typeof data === "string") {
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    blob = new Blob([bytes]);
  } else {
    blob = new Blob([data]);
  }
  return new File([blob], name, { type: blob.type || "application/octet-stream" });
}

function getFileFromValue(value: unknown): File | null {
  if (!value) return null;

  const maybeEvent = value as { target?: { files?: FileList } };
  if (maybeEvent?.target?.files) {
    return maybeEvent.target.files[0] ?? null;
  }

  if (value instanceof File) {
    return value;
  }

  if (value instanceof FileList) {
    return value[0] ?? null;
  }

  const maybeBase64 = value as { name?: string; data?: string | ArrayBuffer };
  if (
    maybeBase64?.name != null &&
    (typeof maybeBase64.data === "string" || maybeBase64.data instanceof ArrayBuffer)
  ) {
    return base64OrBufferToFile(maybeBase64.name, maybeBase64.data);
  }

  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (first instanceof File) return first;
    const obj = first as { name?: string; data?: string | ArrayBuffer };
    if (obj?.name != null && (typeof obj.data === "string" || obj.data instanceof ArrayBuffer)) {
      return base64OrBufferToFile(obj.name, obj.data);
    }
  }

  return null;
}

export const DniFinish: React.FC<DniFinishPropsType> = ({
  back,
  handleSubmit,
  countryCode,
  photoKey,
  signKey,
  relationships,
  setPhotoFile,
  setSignFile,
  onPhotoUpload,
  onSignUpload,
  proofOfDeliveryRequired,
  showSerialNumberInput,
  expectedSerialCode,
  testId,
}): JSX.Element => {
  const { t } = useTranslation();

  const requiredList = proofOfDeliveryRequired || [];
  const requiresPhoto = requiredList.includes("PHOTO");
  const requiresSign = requiredList.includes("SIGN");
  const requiresIdentificationNumber = requiredList.includes("IDENTIFICATION_NUMBER");
  const requiresRelationship = requiredList.includes("RELATIONSHIP");
  const requiresName = requiredList.includes("NAME");
  const requiresLastName = requiredList.includes("LAST_NAME");
  const requiresSerialNumber = showSerialNumberInput;

  const validatePhotoCallback = useCallback(
    (value: unknown) => {
      const file = getFileFromValue(value);
      if (file && validateFile(file)) {
        setPhotoFile(file);
        onPhotoUpload?.(file);
      } else {
        setPhotoFile(file);
      }
    },
    [setPhotoFile, onPhotoUpload]
  );

  const validateSignCallback = useCallback(
    (value: unknown) => {
      const file = getFileFromValue(value);
      if (file && validateFile(file)) {
        setSignFile(file);
        onSignUpload?.(file);
      } else {
        setSignFile(file);
      }
    },
    [setSignFile, onSignUpload]
  );

  const maxLengthDNI = useMemo(
    () => ({ AR: 8, CO: 15, MX: 18, CL: 10 }),
    []
  );

  const serialNumberOptions = useMemo(() => {
    const codes = expectedSerialCode;
    if (codes == null) return [];
    const list = Array.isArray(codes)
      ? codes.filter((c): c is string => typeof c === "string" && c.trim() !== "")
      : String(codes).trim() !== ""
        ? [String(codes).trim()]
        : [];
    return list.map((value) => ({ value, label: value }));
  }, [expectedSerialCode]);

  const allRequiredFilesPresent =
    (!requiresPhoto || photoKey != null) && (!requiresSign || signKey != null);

  return (
    <div className="container-finish">
      <BackButton back={back} />
      <div className="content-finish">
        <img className="modal-dni-finish-card" src={Card} alt="Card" />
        <h3 className="modal-dni-title">
          {t("detailTransaction:dniReceiver")}
        </h3>
        <Form
          subscription={{ errors: true, invalid: true, pristine: true, values: true, submitFailed: true }}
          onSubmit={(values: DniFinishFormValuesType) => handleSubmit(values)}
          validate={useValidationSchema(
            validationSchema(countryCode, {
              requiresSerialNumber,
              requiresPhoto,
              requiresSign,
              requiresName,
              requiresLastName,
              requiresIdentificationNumber,
              requiresRelationship,
              expectedSerialCode,
            })
          )}
          mutators={{
            setValue: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
          }}
        >
          {({ handleSubmit: formSubmit, invalid, pristine, errors, submitFailed }) => (
            <form onSubmit={formSubmit} className="dni-finish-form-wrap">
              <div className={`${requiredList.length > 2 ? "dni-finish-form"  : "dni-finish-form-required"}`}>
                {requiresName && (
                  <div className="dni-input">
                  <Field
                    name="name"
                    component={TextInput}
                    placeholder={t("detailTransaction:namePlaceholder")}
                    className="Admin-Pickers-input"
                    id="name"
                    label={t("detailTransaction:nameLabel")}
                    maxLength={100}
                    testId={`${testId}-name`}
                  />
                </div>
                )}
                {requiresLastName && (
                <div className="dni-input">
                  <Field
                    name="lastName"
                    component={TextInput}
                    placeholder={t("detailTransaction:lastNamePlaceholder")}
                    className="Admin-Pickers-input"
                    label={t("detailTransaction:lastNameLabel")}
                    maxLength={100}
                    testId={`${testId}-last-name`}
                  />
                </div>
                )}
                {requiresIdentificationNumber && (
                  <div className="dni-input">
                    <Field
                      name="identificationNumber"
                      component={TextInput}
                      placeholder={t("detailTransaction:dniPlaceholder")}
                      id="dni"
                      maxLength={maxLengthDNI[countryCode]}
                      label={t("detailTransaction:dniLabel")}
                    />
                  </div>
                )}
                {showSerialNumberInput && (
                  <div className="dni-input">
                    <Field
                      name="serialNumber"
                      className="Admin-Pickers-input"
                      id="serialNumber"
                      label={serialNumberOptions.length > 1 ? t("detailTransaction:serialNumbersLabelRequired") : t("detailTransaction:serialNumberLabelRequired")}
                      placeholder={serialNumberOptions.length > 1 ? t("detailTransaction:serialNumbersPlaceholder") : t("detailTransaction:serialNumberPlaceholder")}
                      options={serialNumberOptions}
                      overAll
                      testId={`${testId}-serial-number`}
                    >
                      {(props: any) => (
                        <MultiSelectInput
                          {...props}
                          keyProperty="value"
                          showProperty="label"
                          overlayPosition="bottom"
                        />
                      )}
                    </Field>
                    <p className={errors?.serialNumber && submitFailed ? "dni-finish-serial-number-helper-text-hidden" : "dni-finish-serial-number-helper-text"}>
                      {serialNumberOptions.length > 1
                        ? t("detailTransaction:serialNumberHelperTextPlural", { count: serialNumberOptions.length })
                        : t("detailTransaction:serialNumberHelperTextSingular", { count: serialNumberOptions.length })}
                    </p>
                  </div>
                )}
                {requiresRelationship && (
                  <div className="dni-input">
                  <Field
                    type="text"
                    name="relationship"
                    className="Admin-Pickers-input"
                    label={t("detailTransaction:relationshipLabel")}
                    placeholder={t("detailTransaction:relationshipPlaceholder")}
                    options={relationships}
                    overAll
                    testId={`${testId}-relationship`}
                  >
                    {(props: any) => (
                      <SelectInput
                        {...props}
                        keyProperty="tag"
                        showProperty="value"
                      />
                    )}
                  </Field>
                </div>
                )}
                {requiresSign && (
                  <div className="dni-finish-file-block">
                    <Field name="sign">
                      {({ input, meta }) => {
                        const handleSignChange = (value: unknown) => {
                          input.onChange(value);
                          validateSignCallback(value);
                        };
                        return (
                          <>
                            <FileInput
                              input={{
                                ...input,
                                onChange: handleSignChange,
                              }}
                              meta={meta}
                              label="Firma *"
                              fileTypes={FILE_TYPES_ACCEPTED}
                              placeholder="Adjuntar archivo"
                              className="dni-finish-file-input"
                              name="sign"
                            />
                          </>
                        );
                      }}
                    </Field>
                  </div>
                )}
                {requiresPhoto && (
                  <div className="dni-finish-file-block">
                    <Field name="photo">
                      {({ input, meta }) => {
                        const handlePhotoChange = (value: unknown) => {
                          input.onChange(value);
                          validatePhotoCallback(value);
                        };
                        return (
                          <>
                            <FileInput
                              input={{
                                ...input,
                                onChange: handlePhotoChange,
                              }}
                              meta={meta}
                              label="Foto *"
                              fileTypes={FILE_TYPES_ACCEPTED}
                              placeholder="Adjuntar archivo"
                              className="dni-finish-file-input"
                              name="photo"
                            />
                          </>
                        );
                      }}
                    </Field>
                  </div>
                )}
                <div className="dni-finish-submit">
                  <Button
                    type="submit"
                    label={t("detailTransaction:finish")}
                    disabled={invalid || pristine || !allRequiredFilesPresent}
                    size="large"
                    fullWidth
                    testId={`${testId}-finish`}
                  />
                </div>
              </div>
            </form>
          )}
        </Form>
      </div>
    </div>
  );
};
